import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast, WarningToast } from 'components/Toast';
import { STRINGS } from 'config';
import React from 'react';
import request, { Response } from 'superagent';

const { HOST } = STRINGS.SUMMARISER;

export enum PromptType {
  REFERRAL = 'referral',
  PATIENT = 'patient',
}

const {
  COPILOT: {
    UPDATE_QUERY,
    CLEAR_COPILOT_RESPONSE,
    COPILOT_QUERY_REQUEST,
    COPILOT_QUERY_SUCCESS,
    COPILOT_QUERY_FAILURE,
    RESET_COPILOT_QUERY_AND_RESPONSE,
    COPILOT_LETTER_SUMMARY_SUCCESS,
    CLEAR_LETTER_SUMMARY,
  },
} = STRINGS.REDUCERS;

type CopilotQueryPayload = {
  consultationId: string;
  transcription: string[];
  summary: string;
  query: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CopilotQueryResponse = any;

type CopilotQueryAction =
  | { type: typeof COPILOT_QUERY_REQUEST }
  | { type: typeof COPILOT_QUERY_SUCCESS; payload: CopilotQueryResponse }
  | { type: typeof COPILOT_QUERY_FAILURE; error: string }
  | { type: typeof UPDATE_QUERY; payload: string }
  | { type: typeof RESET_COPILOT_QUERY_AND_RESPONSE; payload: null }
  | { type: typeof COPILOT_LETTER_SUMMARY_SUCCESS; payload: string }
  | { type: typeof CLEAR_LETTER_SUMMARY };

export const updateMessage = (message: string): CopilotQueryAction => ({
  type: UPDATE_QUERY,
  payload: message,
});

export const fetchCopilotQuery = async (
  dispatch: React.Dispatch<CopilotQueryAction>,
  payload: CopilotQueryPayload,
  selectedPracticeId: string,
): Promise<Response | void> => {
  try {
    dispatch({ type: COPILOT_QUERY_REQUEST });
    const response: Response = await request
      .post(`${HOST}/copilot/query?practiceId=${selectedPracticeId}`)
      .set('Content-Type', 'application/json')
      .send(payload)
      .withCredentials();

    if (response.statusCode === 204) {
      dispatch({
        type: COPILOT_QUERY_SUCCESS,
        payload: '',
      });
      return toast.warning(
        React.createElement(WarningToast, {
          message:
            'Sorry, Huma intelligence cannot help with this. Please try rephrasing your question or ask something else.',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    }

    dispatch({
      type: COPILOT_QUERY_SUCCESS,
      payload: response.body?.edit,
    });

    return response;
  } catch (error: unknown) {
    let message = 'Something went wrong';
    if (error && typeof error === 'object' && 'message' in error) {
      message = (error as Error).message;
    }
    toast.error(
      React.createElement(ErrorToast, {
        message: 'Sorry, we could not process your request. Please try again.',
      }),
      {
        autoClose: 12000,
        pauseOnHover: true,
        hideProgressBar: false,
        closeOnClick: true,
        icon: false,
      },
    );
    return dispatch({
      type: COPILOT_QUERY_FAILURE,
      error: message,
    });
  }
};

export const clearCopilotResponse = (): CopilotQueryAction => ({
  type: RESET_COPILOT_QUERY_AND_RESPONSE,
  payload: '',
});

export const addToSummary = (copilotQueryResponse: string | null | undefined): CopilotQueryAction => ({
  type: UPDATE_QUERY,
  payload: copilotQueryResponse,
});

export const resetCopilotQueryResult = (): CopilotQueryAction => ({
  type: CLEAR_COPILOT_RESPONSE,
  payload: null,
});

export const clearLetterSummary = (): CopilotQueryAction => ({
  type: CLEAR_LETTER_SUMMARY,
});

export async function generateCopilotSummary(
  currentSummary: string,
  selectedPracticeId: string,
  dispatch: React.Dispatch<CopilotQueryAction>,
  promptType: PromptType,
  letter: boolean,
): Promise<string | null> {
  dispatch({ type: COPILOT_QUERY_REQUEST });
  try {
    const body = { summary: currentSummary, promptType, letter };
    const response = await request
      .post(`${HOST}/copilot/extrasummary?practiceId=${selectedPracticeId}`)
      .withCredentials()
      .send(body);
    if (response.status === 200) {
      const { summary } = response.body;
      if (letter) {
        dispatch({ type: COPILOT_LETTER_SUMMARY_SUCCESS, payload: summary });
      } else {
        dispatch({ type: COPILOT_QUERY_SUCCESS, payload: summary });
      }
      return summary;
    }

    if (response.status === 204) {
      if (letter) {
        dispatch({ type: COPILOT_LETTER_SUMMARY_SUCCESS, payload: null });
      } else {
        dispatch({ type: COPILOT_QUERY_SUCCESS, payload: null });
      }
      return null;
    }
  } catch (error) {
    dispatch({ type: COPILOT_QUERY_FAILURE, error: 'Error generating summary' });
    toast.error(
      React.createElement(ErrorToast, {
        message: 'Sorry, we could not generate the summary. Please try again.',
      }),
      {
        autoClose: 12000,
        pauseOnHover: true,
        hideProgressBar: false,
        closeOnClick: true,
        icon: false,
      },
    );
  }
  return null;
}
